import { withRouter, type WithRouterProps } from "adapters/withRouter/withRouter";
import { type AssignAccountPack } from "features/Accounts/PackAssignmentModal/types";
import { Button } from "components/buttons/button/Button";
import Segments from "components/navigation/segments/Segments";
import { bindAction, type IObservable, type MessageIdsType } from "interfaces";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { intersection, isEmpty } from "lodash";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes } from "react-router";
import { bindActionCreators } from "redux";
import { Loader } from "semantic-ui-react";
import { Observable } from "utils";
import { type FiltersMap, type GenericFiltersMap } from "utils/filterUtils";
import { type Filters } from "utils/queryUtils";
import { RestrictedResource } from "components";
import GenericSectionHeader from "components/genericSectionHeader/GenericSectionHeader";
import { AddOnIds, RolePermissions, RouteNames, SortOptions, ViewType } from "enums";
import navigationUtils from "utils/navigationUtils";
import Restricted from "features/Application/Restricted";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import * as backgroundTasksActions from "features/BackgroundTasks/state/backgroundTasksActions";
import packsDataService from "features/Licensing/Packs/services/packsDataService";
import {
  accountPacksFiltersSlice,
  appliedFiltersSelector as accountPacksAppliedFiltersSelector,
  filterOptionsSelector as accountPacksFilterOptionsSelector,
  isLoadingSelector as accountPacksFilterIsLoadingSelector,
  resetAppliedFilter as accountPacksResetAppliedFilter,
  setAppliedFilter as accountPacksSetAppliedFilter,
} from "features/Licensing/Packs/state/slices/accountPacksFiltersSlice";
import {
  accountPacksSearchSelector,
  setAccountPacksSearchTerm,
} from "features/Licensing/Packs/state/slices/accountPacksSearchSlice";
import { resetAccountPacksGrid } from "features/Accounts/state/slices/accountPacksSlice";
import {
  appliedFiltersSelector as availablePacksAppliedFiltersSelector,
  availablePacksFiltersSlice,
  filterOptionsSelector as availablePacksFilterOptionsSelector,
  isLoadingSelector as availablePacksFilterIsLoadingSelector,
  resetAppliedFilter as availablePacksResetAppliedFilter,
  setAppliedFilter as availablePacksSetAppliedFilter,
  setSearch as availablePacksSetSearch,
} from "features/Accounts/state/slices/availablePacksFiltersSlice";
import { reset } from "features/Accounts/state/slices/availablePacksSlice";
import { addPacksToAccount, fetchAccountPacksGrid } from "features/Accounts/state/thunks/accountPacksThunk";
import { fetchAvailablePacks } from "features/Accounts/state/thunks/availablePacksThunk";
import { getFilterOptions } from "../../../Licensing/Packs/state/thunks/packsFiltersThunk";
import { type PacksRequest } from "../../../Licensing/Packs/types/requests";
import * as notificationsActions from "../../../Notifications/state/notificationsActions";
import * as accountInformationActions from "../../AccountInformation/state/accountInformationActions";
import EditPublicApi from "../../Api/EditPublicApi/EditPublicApi";
import IdentityProvidersOverview from "../../IdentityProvidersOverview/IdentityProvidersOverview";
import IntegrationsOverview from "../../Integrations/IntegrationsOverview/IntegrationsOverview";
import accountsDataService from "../../services/accountsDataService";
import {
  AccountTypes,
  type AccountConfigurationInfo,
  type AccountPackLicense,
  type SavingAddOn,
  type AvailablePacksRequest,
} from "../../types";
import EditAddOns from "../EditAddOns/EditAddOns";
import EditContacts from "../EditContacts/EditContacts";
import EditPacks from "../EditPacks/EditPacks";
import * as editAccountActions from "../state/editAccountActions";
import { permissionsToAccountSelector } from "../state/slices/permissionsToAccountSlice";
import { fetchPermissionsToAccount } from "../state/thunks/permissionsToAccountThunk";
import { fetchPotentialContacts } from "../state/thunks/potentialContactsThunk";
import SubscriptionAgreement from "../subscriptionAgreement/SubscriptionAgreement";
import { FeatureFlags } from "featureFlags";
import {
  selectHasTemplatesTabAccess,
  selectHasTemplateUsersTabAccess,
} from "features/Accounts/state/selectors/accountSelectors";
import { pluralize } from "utils/stringUtils";
import { BasicLinkButton, DeleteLinkButton } from "components/buttons/linkButtons";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { type LDProps } from "../../../LDProps";
import moboConfigurator from "moboConfigurator";
import DeleteAccountConfirmationModal from "../../AllAccountsOverview/DeleteAccountModal/DeleteAccountModal";
import { deleteAccountThunk } from "../../AllAccountsOverview/state/thunks/allAccountsOverviewThunk";
import { reset as resetParents } from "../../state/slices/parentAccountSlice";
import { fetchParentAccounts } from "../../state/thunks/parentAccountThunk";
import {
  setSelected as setSelectedUsers,
  reset as resetTemplateUsers,
} from "features/Accounts/state/slices/editAccountUsersSlice";
import EditTemplates from "../Templates/EditTemplates";
import EditAccountUsersList from "../TemplateUsers/EditAccountUsersList";
import { fetchAccountsTags as fetchAccountsTagsAction } from "features/Accounts/state/thunks/accountsLabelsThunk";
import styles from "../styles/EditAccount.module.scss";
import cn from "classnames";
import EmailSettingsContainer from "features/Accounts/EmailSettings/EmailSettingsContainer";
import EditAccountConfiguration from "features/Accounts/EditAccount/EditAccountConfiguration/EditAccountConfiguration";
import {
  fetchAddOns as fetchAddOnsThunk,
  updateAddOns as updateAddOnsThunk,
} from "features/Accounts/state/thunks/addOnsThunk";
import { setAddOns, resetAddOns as resetAddOnsAction } from "features/Accounts/state/slices/addOnsSlice";
import { addDomainToAccount } from "features/Accounts/state/thunks/accountDomainsThunk";
import ContentOverviewReports from "features/Reporting/Content/ContentOverviewReports";
import ThreatDefense from "../ThreatDefense/ThreatDefense";
import { AddTemplateUserButton } from "features/Accounts/TemplateUsers/AddTemplateUserButton";
import { faKey } from "@fortawesome/pro-solid-svg-icons";
import RtnEventsEmitter from "features/Application/services/realTimeNotification/rtnEventsEmitter";
import * as accountsEventTypes from "features/Application/services/realTimeNotification/events/accounts/accountsEvents";

const overviewUrl = `/${RouteNames.linkedAccountsManagement}`;
const accountHomePath = `/${RouteNames.accounts}/home`;

const checkAccountType = (state: RootState, accountTypeId: number) => state.userProfile.accountTypeId === accountTypeId;

interface EditAccountState {
  showAddContactsModal: boolean;
  showAddPacksModal: boolean;
  showUsersFileUploadModal: boolean;
  getAvailablePacksForModalRequest?: { abort: () => void };
}

export type EditFullAccountProps = PropsFromRedux & WithRouterProps & LDProps;

export class EditFullAccount extends React.Component<EditFullAccountProps, EditAccountState> {
  private readonly createUserObserver: IObservable<(onConfirm: () => void) => void>;
  private readonly importUserObserver: IObservable<(onConfirm: () => void) => void>;
  private readonly deleteUsersObserver: IObservable<(onConfirm: () => void, ids: number[]) => void>;
  private readonly deleteAccountObserver: IObservable<(onConfirm: () => void, id: number, name: string) => void>;
  constructor(props: EditFullAccountProps) {
    super(props);
    this.state = {
      showAddContactsModal: false,
      showAddPacksModal: false,
      showUsersFileUploadModal: false,
      getAvailablePacksForModalRequest: undefined,
    };
    this.createUserObserver = new Observable<(onConfirm: () => void) => void>();
    this.importUserObserver = new Observable<(onConfirm: () => void) => void>();
    this.deleteUsersObserver = new Observable<(onConfirm: () => void, ids: number[]) => void>();
    this.deleteAccountObserver = new Observable<(onConfirm: () => void, id: number, name: string) => void>();
  }
  private static readonly segmentPaths = {
    configure: "configure",
    legal: "legal",
    contacts: "contacts",
    performance: "performance",
    addOns: "addons",
    packs: "packs",
    integrations: "integrations",
    sso: "sso",
    api: "api",
    emailSettings: "email-settings",
    threatDefense: "threat-defense",
    templateUsers: "template-users",
    templates: "templates",
  };

  componentDidMount() {
    const accountId = this.getAccountId();
    this.props.editAccountActions.fetchAccountInfo(accountId);
    this.props.fetchPermissionsToAccount(accountId);
    this.fetchAddOns();

    if (!this.props.accountsLabelsLoaded) {
      this.props.fetchAccountsTags(false);
    }

    RtnEventsEmitter.subscribe(accountsEventTypes.DeleteAccountOperationSuccess, this.handleAccountDeleted);
  }

  componentDidUpdate(prevProps: EditFullAccountProps) {
    const prevAccountId = (prevProps.params.moboId && parseInt(prevProps.params.moboId)) || prevProps.accountId;
    const currentAccountId = this.getAccountId();

    if (currentAccountId !== prevAccountId) {
      this.fetchAddOns();
    }
  }

  componentWillUnmount() {
    this.props.editAccountActions.resetAccountContacts();
    this.props.resetAddOns();
    this.props.editAccountActions.resetAccountPacksForbidden();
    this.props.resetState();
    RtnEventsEmitter.unsubscribe(accountsEventTypes.DeleteAccountOperationSuccess, this.handleAccountDeleted);
  }

  handleAccountDeleted = () => {
    this.props.navigate(overviewUrl);
  };

  getAccountId = () => {
    const { params, accountId } = this.props;
    return (params.moboId && parseInt(params.moboId)) || accountId;
  };

  isContactsTab() {
    return this.isActiveRoute(EditFullAccount.segmentPaths.contacts);
  }

  isAddOnsTab() {
    return this.isActiveRoute(EditFullAccount.segmentPaths.addOns);
  }

  isPacksTab() {
    return this.isActiveRoute(EditFullAccount.segmentPaths.packs);
  }

  isPerformanceTab() {
    return this.isActiveRoute(EditFullAccount.segmentPaths.performance);
  }

  isUsersTab() {
    return this.isActiveRoute(EditFullAccount.segmentPaths.templateUsers);
  }

  isActiveRoute(route: string) {
    const {
      location: { pathname },
    } = this.props;

    return pathname.includes(route);
  }

  isHomeUrl = () => {
    const { location } = this.props;
    return location.pathname.includes(accountHomePath);
  };

  renderAddContactsButton = () => {
    return (
      <Button className="create-button" onClick={this.onAddContactsButtonClick} primary>
        Add Contact
      </Button>
    );
  };

  renderAddPacksButton = () => {
    return (
      <Button className="create-button" onClick={this.onAddPacksButtonClick} primary>
        Add Pack
      </Button>
    );
  };

  renderAddUserButton = () => {
    const { selected } = this.props.usersList;

    return (
      <AddTemplateUserButton
        shouldRender={selected.length === 0}
        onImportUsers={this.importUserObserver.notify}
        onCreateUser={this.createUserObserver.notify}
      />
    );
  };

  onDeleteSelectedUsers = () => {
    const { selected } = this.props.usersList;
    if (selected.length === 0) {
      return;
    }
    this.deleteUsersObserver.notify(() => {
      this.props.setSelectedUsers([]);
    }, selected);
  };

  renderDeleteUserButton = () => {
    return <DeleteLinkButton onClick={this.onDeleteSelectedUsers} />;
  };

  onOpenMobo = () => {
    moboConfigurator.openMobo(this.getAccountId());
  };

  onDeleteAccount = () => {
    const { accountConfiguration } = this.props;
    this.deleteAccountObserver.notify(this.confirmDeleteAccount, this.getAccountId(), accountConfiguration.name);
  };

  confirmDeleteAccount = () => {
    const { accountConfiguration, deleteAccount } = this.props;
    deleteAccount(this.getAccountId(), accountConfiguration.name);
  };

  renderDropdownButtons = () => {
    const { userPermissions } = this.props;
    const canDelete = userPermissions.includes(RolePermissions.AccountsSettingsManage);

    return [
      <BasicLinkButton key="manage-account" onClick={this.onOpenMobo} text="Manage" icon={faKey} />,
      <DeleteLinkButton key="delete-account" onClick={this.onDeleteAccount} isDisabled={!canDelete} />,
    ];
  };

  renderHeaderButtons = () => {
    const { userPermissions, contactsList, params, isPacksAccessAuthorized } = this.props;
    const isSettingsManage = userPermissions.includes(RolePermissions.AccountsSettingsManage);

    const buttons: JSX.Element[] = [];

    const addButton = (condition: boolean, renderFn: () => JSX.Element) => {
      if (condition) {
        buttons.push(renderFn());
      }
    };

    addButton(
      this.isContactsTab() && isSettingsManage && contactsList.isAccessAuthorized,
      this.renderAddContactsButton,
    );

    addButton(
      this.isPacksTab() && isPacksAccessAuthorized && !!params.moboId && isSettingsManage,
      this.renderAddPacksButton,
    );

    addButton(this.isUsersTab(), this.renderAddUserButton);

    const { selected } = this.props.usersList;
    addButton(this.isUsersTab() && selected.length > 0, this.renderDeleteUserButton);

    return buttons;
  };

  onAddContactsButtonClick = () => {
    this.setState({ showAddContactsModal: true });
  };

  onAddPacksButtonClick = () => {
    this.setState({ showAddPacksModal: true });
  };

  onCloseAddPacksModal = () => {
    this.setState({ showAddPacksModal: false });
  };

  onCloseAddContactsModal = () => {
    this.props.editAccountActions.resetAddContactsSearch();
    this.setState({ showAddContactsModal: false });
  };

  /* istanbul ignore next */
  onConfirmAddContactsModal = (userIds: number[]) => {
    this.onCloseAddContactsModal();

    const accountId = this.getAccountId();
    const userInMessageSingular = userIds.length === 1;
    const params = {
      id: "AddContactsToAccount",
      title: "Add contacts to account",
      indeterminate: true,
      getMessageIds: async () => {
        const executionId = await accountsDataService.addAccountContacts(accountId, userIds);
        return [executionId];
      },
      successTransientMessage: userInMessageSingular
        ? "Contact has been added to account successfully"
        : "Contacts have been added to account successfully",
      failureTransientMessage: userInMessageSingular
        ? "Adding contact to account failed!"
        : "Adding contacts to account failed!",
    };

    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  refreshPacksList = (accountId: number, selectedViewType: ViewType) => {
    if (selectedViewType === ViewType.GRID) {
      const { accountPacksAppliedFilter, accountPacksActions } = this.props;

      accountPacksActions.resetAccountPacksGrid();
      accountPacksActions.fetchAccountPacksGrid({
        accountId,
        skip: 0,
        orderBy: SortOptions.Title,
        filters: accountPacksAppliedFilter,
        searchTerm: this.props.accountPacksSearch.term,
      });
    }
  };

  triggerRefreshPacksGrid = (viewType: ViewType) => {
    this.refreshPacksList(this.getAccountId(), viewType);
  };

  /* istanbul ignore next */
  onConfirmAddPacksModal = async (packs: AssignAccountPack[], selectedViewType: ViewType) => {
    this.setState({ showAddPacksModal: false });
    const accountId = this.getAccountId();
    this.props.addPacksToAccount(packs, accountId, () => {
      this.refreshPacksList(accountId, selectedViewType);
    });
    this.props.addDomainToAccount([accountId]);
  };

  /* istanbul ignore next */
  onConfirmEditLicensingModal = (accountPackLicense: AccountPackLicense, selectedViewType: ViewType) => {
    const accountId = this.getAccountId();
    const params = {
      id: "EditPackLicensesForAccount",
      title: "Edit pack licenses for account",
      indeterminate: true,
      getMessageIds: async () => {
        const response = await packsDataService.updateAccountPackLicenseAsync(accountId, {
          id: accountPackLicense.packAccountLicensesId,
          expirationDate: accountPackLicense.expirationDate,
          licensesCount: accountPackLicense.totalLicensesCount,
          licenseType: accountPackLicense.licenseTypeId,
        });
        return [response.data];
      },
      successTransientMessage: "Licensing updated successfully.",
      failureTransientMessage: "Pack renewal failed!",
      onCompleted: () => {
        this.refreshPacksList(accountId, selectedViewType);
      },
    };

    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  /* istanbul ignore next */
  onConfirmExtendLicensesModal = (packs: AssignAccountPack[], selectedViewType: ViewType) => {
    const accountId = this.getAccountId();
    const params = {
      id: "ExtendPackLicensesToAccount",
      title: "Edit pack licenses for account",
      indeterminate: true,
      getMessageIds: async () => {
        const response = await packsDataService.extendPacksLicensesToAccountsAsync(accountId, {
          packInfos: packs,
        });
        return [response.data];
      },
      successTransientMessage: "Licensing updated successfully.",
      failureTransientMessage: "Pack renewal failed!",
      onCompleted: () => {
        this.refreshPacksList(accountId, selectedViewType);
      },
    };

    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  /* istanbul ignore next */
  onConfirmRestorePackModal = (packs: AssignAccountPack[], selectedViewType: ViewType) => {
    const accountId = this.getAccountId();
    const params = {
      id: "RestorePackForAccount",
      title: "Restore pack for account",
      indeterminate: true,
      getOperationProps: async () => {
        const response = await packsDataService.restoreAccountPackAsync(accountId, {
          packInfos: packs,
        });
        return response.data;
      },
      successTransientMessage: "Pack restored successfully.",
      failureTransientMessage: "Pack restoring failed!",
      onCompleted: () => {
        this.refreshPacksList(accountId, selectedViewType);
      },
    };

    const {
      backgroundTasksActions: { addOperationDistributedOp },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntityDistributedOp(params, {
      addOperation: addOperationDistributedOp,
      sendTransientNotification,
    });
  };

  /* istanbul ignore next */
  onConfirmConvertTrialPackModal = (packs: AssignAccountPack[], selectedViewType: ViewType) => {
    const accountId = this.getAccountId();
    const pack = packs[0];
    const params = {
      id: "ConvertTrialPackLicenseForAccount",
      title: "Edit pack licenses for account",
      indeterminate: true,
      getOperationProps: async () => {
        const response = await packsDataService.convertTrialAccountPackAsync(accountId, pack.packId, {
          licenseTypeId: pack.licenseType,
          licenseCount: pack.licensesCount,
          expirationDate: pack.expirationDate,
        });
        return response.data;
      },
      successTransientMessage: "Licensing updated successfully.",
      failureTransientMessage: "Pack converting failed!",
      onCompleted: () => {
        this.refreshPacksList(accountId, selectedViewType);
      },
    };

    const {
      backgroundTasksActions: { addOperationDistributedOp },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntityDistributedOp(params, {
      addOperation: addOperationDistributedOp,
      sendTransientNotification,
    });
  };

  /* istanbul ignore next */
  updateContactsHandler = async (changes: {
    mainContactId?: number;
    unassignPreviousMainContactRoles: boolean;
    removedContactIds: number[];
  }) => {
    const params = {
      id: "UpdateAccountContacts",
      title: "Update contacts",
      indeterminate: true,
      getMessageIds: async () => {
        const messageId = await accountsDataService.updateAccountContacts(this.getAccountId(), changes);
        this.props.editAccountActions.updateAccountContacts({
          mainContactId: changes.mainContactId,
          removedContactIds: changes.removedContactIds,
        });
        return [messageId];
      },
      successTransientMessage: "Contacts have been updated successfully",
      failureTransientMessage: "Contacts update have been failed",
    };
    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  /* istanbul ignore next */
  updateAccountInfoHandler = (account: AccountConfigurationInfo, accountId: number, isReadOnly: boolean) => {
    const params = {
      id: "UpdateAccountInformation",
      title: "Update account information",
      indeterminate: true,
      getMessageIds: async (): Promise<MessageIdsType> => {
        const messageId = isReadOnly
          ? await accountsDataService.updateOwnAccount(account)
          : await accountsDataService.updateAccountInfo(accountId, account);
        this.props.editAccountActions.updateAccountInfo(account);
        if (this.isHomeUrl()) this.props.accountInformationActions(account);
        return [messageId];
      },
      successTransientMessage: "Account information update succeeded!",
      failureTransientMessage: "Account information update failed!",
    };

    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntity(params, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  updateAddOns = (addOns: Array<SavingAddOn>) => {
    const accountId = this.getAccountId();
    this.props.updateAddOns({ accountId, addOns });
    this.props.updateAccountAddOnsState(addOns);
  };

  fetchAddOns = () => {
    const accountId = this.getAccountId();
    this.props.fetchAddOns(accountId);
  };

  fetchAccountContacts = (accountId: number, skip: number, top: number, orderParams: string, filterParams: string) => {
    this.props.editAccountActions.fetchAccountContacts(accountId, skip, top, orderParams, filterParams);
  };

  markTabAsReadOnly = () => {
    const isReadOnly = (viewPermission: RolePermissions, managePermission: RolePermissions) => {
      const { userPermissions } = this.props;
      return userPermissions.includes(viewPermission) && !userPermissions.includes(managePermission);
    };

    return this.isAddOnsTab()
      ? isReadOnly(RolePermissions.AddonsView, RolePermissions.AddonsManage)
      : isReadOnly(RolePermissions.AccountsSettingsView, RolePermissions.AccountsSettingsManage);
  };

  loadPacksForModal = (assignedAccountId: number, skip: number, orderBy: string, filters?: Filters) => {
    this.state.getAvailablePacksForModalRequest && this.state.getAvailablePacksForModalRequest?.abort();
    const request = this.props.packsModalActions.fetchItems({
      assignedAccountId,
      skip,
      orderBy,
      filters,
      searchTerm: this.props.availablePacksSearch,
      allowAppend: true,
    });
    this.setState({ getAvailablePacksForModalRequest: request });
  };

  loadPackForModal = (assignedAccountId: number, packIds: number[]) => {
    this.state.getAvailablePacksForModalRequest && this.state.getAvailablePacksForModalRequest?.abort();
    const request = this.props.packsModalActions.fetchItems({
      assignedAccountId,
      skip: 0,
      filters: { primaryId: packIds?.length ? packIds[0] : 0 },
    });
    this.setState({ getAvailablePacksForModalRequest: request });
  };

  /* istanbul ignore next */
  resetPacksModal = () => {
    const { packsModalActions } = this.props;
    packsModalActions.resetGrid();
  };

  resetPackModalFilter = () => {
    this.props.resetPackModalFilter();
  };

  applyPackModalFilter = (filter: Filters) => {
    this.props.applyPackModalFilter(filter);
  };

  resetAccountPacksFilter = () => {
    this.props.resetAccountPacksFilter();
  };

  applyAccountPacksFilter = (filter: Filters) => {
    this.props.applyAccountPacksFilter(filter);
  };

  getPackModalFilterOptions = () => {
    this.props.getPackModalFilterOptions();
  };

  getAccountPacksFilterOptions = () => {
    this.props.getAccountPacksFilterOptions();
  };

  isOwnAccount = () => {
    const { isMobo, accountId } = this.props;
    return !isMobo && accountId === this.getAccountId();
  };

  hasPermissionsToAccountPredicate =
    (userPermissions: RolePermissions[]) => (_: unknown, requiredPermissions?: RolePermissions[]) =>
      !!requiredPermissions && intersection(userPermissions, requiredPermissions).length > 0;

  getTitle = (): string => {
    const { selected } = this.props.usersList;

    if (this.isUsersTab() && selected.length > 0) {
      return `${selected.length} ${pluralize("User", selected.length)} Selected`;
    }

    return !this.isHomeUrl() ? this.props.accountConfiguration.name : "Account Settings";
  };

  getTitleClassname = () => {
    const { selected } = this.props.usersList;
    if (this.isUsersTab() && selected.length > 0) {
      return `selected-items-label`;
    }
  };

  render() {
    const {
      accountConfiguration,
      isLoadingAccountTypes,
      isLoadingPermissionsToAccount,
      isLoading,
      isAccessAuthorizedForInfo,
      contactsList,
      packsList,
      packsGrid,
      onFetchPotentialContacts,
      editAccountActions: { fetchAccountPacks, setAddContactsSearch, resetAccountPacksList },
      potentialContacts,
      addOns,
      packsModalFilterOptions,
      packsModalAppliedFilter,
      packsModalFilterIsLoading,
      accountPacksFilterOptions,
      accountPacksAppliedFilter,
      accountPacksFilterIsLoading,
      packsModal,
      availablePacksSearch,
      accountPacksActions,
      accountPacksSearch,
      addContactsSearch,
      userPermissions,
      location,
      navigate,
      areAllLoaded,
    } = this.props;
    const smtpDirectSend = !!this.props.flags?.[FeatureFlags.SmtpDirectSend];
    const accountReportEnabled = !!this.props.flags?.[FeatureFlags.AccountReport];

    const { showAddContactsModal, showAddPacksModal } = this.state;

    const isInfoLoading = isLoading || isLoadingPermissionsToAccount;
    const accountId = this.getAccountId();

    const basePath = this.isHomeUrl() ? accountHomePath : `${overviewUrl}/${accountId}`;
    let isFiltered = !isEmpty(packsModalAppliedFilter) || !isEmpty(availablePacksSearch);

    const checkShouldRenderTemplateUsersTab = () => {
      if (this.isHomeUrl() || this.props.hasChannelPartnerAccountType) {
        return false;
      }

      return this.props.hasTemplateUsersTabAccess;
    };

    const checkShouldRenderTemplatesTab = () => {
      if (this.isHomeUrl()) {
        return false;
      }

      return isAccessAuthorizedForInfo && this.props.hasTemplatesTabAccess;
    };

    const segments: { path: string | null; label: string; shouldRender: boolean; end?: boolean }[] = !isInfoLoading
      ? [
          {
            path: `${EditFullAccount.segmentPaths.performance}/content`,
            label: "Performance",
            shouldRender: !this.isHomeUrl() && accountReportEnabled,
            end: false,
          },
          {
            path: this.isHomeUrl() ? null : EditFullAccount.segmentPaths.configure,
            label: "Configuration",
            shouldRender: true,
          },
          {
            path: EditFullAccount.segmentPaths.templateUsers,
            label: "Template Users",
            shouldRender: checkShouldRenderTemplateUsersTab(),
          },
          {
            path: EditFullAccount.segmentPaths.templates,
            label: "Templates",
            shouldRender: checkShouldRenderTemplatesTab(),
          },
          { path: EditFullAccount.segmentPaths.packs, label: "Packs", shouldRender: true },
          { path: EditFullAccount.segmentPaths.addOns, label: "Add-ons", shouldRender: true },
          { path: EditFullAccount.segmentPaths.contacts, label: "Contacts", shouldRender: true },
          { path: EditFullAccount.segmentPaths.legal, label: "Legal", shouldRender: true },
          {
            path: EditFullAccount.segmentPaths.emailSettings,
            label: "Communication Preferences",
            shouldRender: smtpDirectSend,
          },
          {
            path: EditFullAccount.segmentPaths.threatDefense,
            label: "Threat Defense",
            shouldRender: this.props.userPermissions.includes(RolePermissions.CampaignsManage),
          },
          {
            path: EditFullAccount.segmentPaths.integrations,
            label: "Integrations",
            shouldRender: true,
          },
          { path: EditFullAccount.segmentPaths.sso, label: "SSO", shouldRender: true },
          { path: EditFullAccount.segmentPaths.api, label: "API", shouldRender: true },
        ]
      : [];

    const { selected } = this.props.usersList;

    const packsModalSearch = {
      term: availablePacksSearch,
    };

    const editAccountConfigurationSection = (
      <RestrictedResource isAuthorized={isAccessAuthorizedForInfo}>
        <EditAccountConfiguration
          accountId={accountId}
          onInfoUpdated={this.updateAccountInfoHandler}
          isOwnAccount={this.isOwnAccount()}
          isLoading={isInfoLoading || isLoadingAccountTypes}
        />
      </RestrictedResource>
    );

    return (
      <section className={cn(styles["edit-account"], "nested-content")}>
        <GenericSectionHeader
          title={this.getTitle()}
          titleForGA="Account Details"
          goBackAction={() => navigationUtils.goBackOrDefault(location, navigate, overviewUrl)}
          showGoBackIcon={!this.isHomeUrl() && selected.length <= 0}
          dropdownButtons={
            this.isActiveRoute(EditFullAccount.segmentPaths.performance) ? this.renderDropdownButtons() : null
          }
          buttons={this.renderHeaderButtons()}
          isReadOnly={!this.isOwnAccount() && this.markTabAsReadOnly()}
          titleClassname={this.getTitleClassname()}
          showReportLastUpdated={this.isPerformanceTab()}
        />

        <Segments to={basePath}>
          {segments.map(
            (segment, index) =>
              segment.shouldRender && (
                <Segments.Segment to={segment.path} label={segment.label} end={segment.end} key={index} />
              ),
          )}
        </Segments>

        <Routes>
          {this.isHomeUrl() && <Route path="/" element={editAccountConfigurationSection} />}
          {!this.isHomeUrl() && accountReportEnabled && (
            <Route
              path={`${EditFullAccount.segmentPaths.performance}/content/*`}
              element={
                <ContentOverviewReports
                  childAccountId={accountId}
                  hasCreateAccountsAddOn={
                    Array.isArray(addOns?.items) &&
                    addOns.items.some((addOn) => addOn.id === AddOnIds.CreateAccounts && addOn.isEnabled)
                  }
                  usesDropdownMenuNavigation
                />
              }
            />
          )}
          {!this.isHomeUrl() && (
            <Route path={EditFullAccount.segmentPaths.configure} element={editAccountConfigurationSection} />
          )}
          {!this.isHomeUrl() && !accountReportEnabled && (
            <Route index element={<Navigate to="./configure" replace />} />
          )}
          <Route path={EditFullAccount.segmentPaths.legal} element={<SubscriptionAgreement accountId={accountId} />} />
          <Route
            path={EditFullAccount.segmentPaths.contacts}
            element={
              <RestrictedResource isAuthorized={contactsList.isAccessAuthorized}>
                <EditContacts
                  accountId={accountId}
                  contactsList={contactsList}
                  onFetchContacts={this.fetchAccountContacts}
                  fetchPotentialContacts={onFetchPotentialContacts}
                  onCloseAddContactsModal={this.onCloseAddContactsModal}
                  onConfirmAddContactsModal={this.onConfirmAddContactsModal}
                  renderAddContactsButton={this.renderAddContactsButton}
                  isReadOnly={!accountConfiguration.canEdit}
                  isAccountInfoLoading={isInfoLoading}
                  showAddContactsModal={showAddContactsModal}
                  potentialContacts={potentialContacts}
                  onContactsUpdated={this.updateContactsHandler}
                  addContactsSearch={addContactsSearch}
                  setAddContactsSearch={setAddContactsSearch}
                  permissionsToAccount={userPermissions}
                />
              </RestrictedResource>
            }
          />
          <Route
            path={EditFullAccount.segmentPaths.packs}
            element={
              <RestrictedResource isAuthorized={this.props.isPacksAccessAuthorized}>
                <EditPacks
                  selectedAccountId={accountId}
                  accountId={accountId}
                  packsList={packsList}
                  packsGrid={packsGrid}
                  onFetchPacks={fetchAccountPacks}
                  onResetPacksList={resetAccountPacksList}
                  onFetchPacksGrid={accountPacksActions.fetchAccountPacksGrid}
                  onResetPacksGrid={accountPacksActions.resetAccountPacksGrid}
                  renderAddPacksButton={this.renderAddPacksButton}
                  showAddPacksModal={showAddPacksModal}
                  isAccountInfoLoading={isInfoLoading}
                  canAddPack={
                    !this.isHomeUrl() && this.props.userPermissions.includes(RolePermissions.AccountsSettingsManage)
                  }
                  hasPurchasePower={this.props.userPermissions.includes(RolePermissions.PacksPurchase)}
                  onCloseAddPacksModal={this.onCloseAddPacksModal}
                  onConfirmAddPacksModal={this.onConfirmAddPacksModal}
                  onConfirmEditLicensingModal={this.onConfirmEditLicensingModal}
                  onConfirmExtendLicensesModal={this.onConfirmExtendLicensesModal}
                  onConfirmRestorePackModal={this.onConfirmRestorePackModal}
                  onConfirmConvertTrialPackModal={this.onConfirmConvertTrialPackModal}
                  addPacksModal={packsModal}
                  loadPacksForModal={this.loadPacksForModal}
                  loadPackForModal={this.loadPackForModal}
                  resetModalGrid={this.resetPacksModal}
                  packsModalSearch={packsModalSearch}
                  setPacksModalSearch={this.props.setAvailablePacksSearch}
                  isPacksModalFiltered={isFiltered}
                  modalFilter={{
                    filterOptions: packsModalFilterOptions,
                    appliedFilter: packsModalAppliedFilter,
                    applyFilter: this.applyPackModalFilter,
                    resetFilter: this.resetPackModalFilter,
                    fetchFilterOptions: this.getPackModalFilterOptions,
                    isLoading: packsModalFilterIsLoading,
                  }}
                  accountPacksFilter={{
                    filterOptions: accountPacksFilterOptions,
                    appliedFilter: accountPacksAppliedFilter,
                    applyFilter: this.applyAccountPacksFilter,
                    resetFilter: this.resetAccountPacksFilter,
                    fetchFilterOptions: this.getAccountPacksFilterOptions,
                    isLoading: accountPacksFilterIsLoading,
                  }}
                  accountPacksSearch={accountPacksSearch}
                  setAccountPacksSearch={accountPacksActions.setAccountPacksSearch}
                  triggerRefreshPacksGrid={this.triggerRefreshPacksGrid}
                  isAccountHome={this.isHomeUrl()}
                  permissionsToAccount={userPermissions}
                  enabled={this.props.isAccessAuthorizedForInfo}
                />
              </RestrictedResource>
            }
          />
          <Route
            path={EditFullAccount.segmentPaths.addOns}
            element={
              <Restricted
                permissionPredicate={this.hasPermissionsToAccountPredicate(userPermissions)}
                permissions={[RolePermissions.AddonsManage]}
                renderContent={(canManage) => (
                  <RestrictedResource isAuthorized={addOns.isAccessAuthorized}>
                    <EditAddOns
                      fetchAddOns={this.fetchAddOns}
                      updateAddOns={this.updateAddOns}
                      addOns={addOns.items}
                      isLoading={addOns.isLoading}
                      isReadOnly={!canManage}
                    />
                  </RestrictedResource>
                )}
              />
            }
          />
          {smtpDirectSend && (
            <Route
              path={EditFullAccount.segmentPaths.emailSettings}
              element={
                <>
                  {areAllLoaded ? (
                    <RestrictedResource
                      isAuthorized={
                        this.props.userPermissions.includes(RolePermissions.AccountsSettingsManage) ||
                        this.props.userPermissions.includes(RolePermissions.AccountsSettingsView) ||
                        this.props.userPermissions.includes(RolePermissions.AccountsSettingsCreate)
                      }
                    >
                      <EmailSettingsContainer accountId={accountId} />
                    </RestrictedResource>
                  ) : (
                    <Loader active />
                  )}
                </>
              }
            />
          )}
          <Route
            path={EditFullAccount.segmentPaths.threatDefense}
            element={
              <>
                {areAllLoaded ? (
                  <RestrictedResource
                    isAuthorized={this.props.userPermissions.includes(RolePermissions.CampaignsManage)}
                  >
                    <ThreatDefense accountId={accountId} />
                  </RestrictedResource>
                ) : (
                  <Loader active />
                )}
              </>
            }
          />

          <Route
            path={EditFullAccount.segmentPaths.integrations}
            element={
              <>
                {areAllLoaded ? (
                  <RestrictedResource
                    isAuthorized={this.props.userPermissions.includes(RolePermissions.AccountsSettingsView)}
                  >
                    <IntegrationsOverview
                      userPermissions={this.props.userPermissions}
                      isReadOnly={!accountConfiguration.canEdit}
                    />
                  </RestrictedResource>
                ) : (
                  <Loader active />
                )}
              </>
            }
          />
          <Route
            path={EditFullAccount.segmentPaths.sso}
            element={
              <>
                {areAllLoaded ? (
                  <RestrictedResource
                    isAuthorized={this.props.userPermissions.includes(RolePermissions.AccountsSettingsView)}
                  >
                    <IdentityProvidersOverview isReadOnly={!accountConfiguration.canEdit} />
                  </RestrictedResource>
                ) : (
                  <Loader active />
                )}
              </>
            }
          />
          <Route
            path={EditFullAccount.segmentPaths.api}
            element={
              <>
                {areAllLoaded ? (
                  <RestrictedResource isAuthorized={this.props.userPermissions.includes(RolePermissions.APIsView)}>
                    <EditPublicApi />
                  </RestrictedResource>
                ) : (
                  <Loader active />
                )}
              </>
            }
          />
          {checkShouldRenderTemplateUsersTab() && (
            <Route
              path={EditFullAccount.segmentPaths.templateUsers}
              element={
                <>
                  {areAllLoaded ? (
                    <EditAccountUsersList
                      editAccountId={accountId}
                      renderAddUsersButton={this.renderAddUserButton}
                      onImportUsersModalObserver={this.importUserObserver}
                      onAddUserModalObserver={this.createUserObserver}
                      onDeleteUsersObserver={this.deleteUsersObserver}
                      isReadOnly={this.markTabAsReadOnly()}
                    />
                  ) : (
                    <Loader active />
                  )}
                </>
              }
            />
          )}
          {checkShouldRenderTemplatesTab() && (
            <Route
              path={EditFullAccount.segmentPaths.templates}
              element={
                <RestrictedResource permissions={[RolePermissions.TemplatesView]}>
                  <EditTemplates accountId={Number(this.props.params.moboId)} title="Templates" />
                </RestrictedResource>
              }
            />
          )}
          {accountReportEnabled ? (
            <Route path="*" element={<Navigate to="./performance/content" replace />} />
          ) : (
            <Route path="*" element={<Navigate to="../" replace />} />
          )}
        </Routes>
        <DeleteAccountConfirmationModal deleteAccountObserver={this.deleteAccountObserver} />
      </section>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { edit, contractTypes, addOns, templateUsers } = state.accounts;
  const { userProfile } = state;

  return {
    isPacksAccessAuthorized: state.accounts.edit.accountPacks.isAccessAuthorized,
    accountId: state.userProfile.accountId,
    accountTypes: edit.accountInfo.accountTypes,
    contractTypes: contractTypes.items,
    accountConfiguration: edit.accountInfo.accountConfiguration,
    isLoadingAccountTypes: edit.accountInfo.isLoadingAccountTypes,
    isLoadingContractTypes: contractTypes.isLoading,
    isLoadingPermissionsToAccount: edit.permissionsToAccount.isLoading,
    isAccessAuthorizedForInfo: edit.accountInfo.isAccessAuthorized,
    isLoading: edit.accountInfo.isLoading,
    error: edit.accountInfo.error,
    contactsList: edit.contactsList,
    potentialContacts: edit.potentialContacts,
    addOns,
    isMobo: userProfile.isMobo,
    hasGlobalAccess: userProfile.hasGlobalAccess,
    hasChannelPartnerAccountType: checkAccountType(state, AccountTypes.ChannelPartner),
    hasTemplateUsersTabAccess: selectHasTemplateUsersTabAccess(state),
    hasTemplatesTabAccess: selectHasTemplatesTabAccess(state),
    userPermissions: permissionsToAccountSelector(state),
    areAllLoaded: state.accounts.edit.permissionsToAccount.areAllLoaded,
    packsList: edit.packsList,
    packsGrid: edit.packsGrid,
    packsModal: state.accounts.availablePacks,
    availablePacksSearch: state.accounts.availablePacksFilters.search,
    packsModalFilterOptions: availablePacksFilterOptionsSelector(state),
    packsModalAppliedFilter: availablePacksAppliedFiltersSelector(state),
    packsModalFilterIsLoading: availablePacksFilterIsLoadingSelector(state),
    accountPacksFilterOptions: accountPacksFilterOptionsSelector(state),
    accountPacksAppliedFilter: accountPacksAppliedFiltersSelector(state),
    accountPacksFilterIsLoading: accountPacksFilterIsLoadingSelector(state),
    accountPacksSearch: accountPacksSearchSelector(state),
    addContactsSearch: edit.addContactsSearch,
    parentAccounts: state.accounts.parentAccounts,
    usersList: templateUsers,
    accountsLabelsLoaded: state.accounts.tags.items.length > 0,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    editAccountActions: bindActionCreators(editAccountActions, dispatch),
    accountInformationActions: bindActionCreators(accountInformationActions.updateData, dispatch),
    onFetchPotentialContacts: bindActionCreators(
      (accountId: number, skip: number, top: number, orderBy: string, filter: FiltersMap, search?: string) =>
        fetchPotentialContacts({ accountId, skip, top, orderBy, filter, search }),
      dispatch,
    ),
    notificationsActions: bindActionCreators(notificationsActions, dispatch),
    backgroundTasksActions: bindActionCreators(backgroundTasksActions, dispatch),
    packsModalActions: {
      resetGrid: () => dispatch(reset()),
      fetchItems: (requestData: AvailablePacksRequest) => dispatch(fetchAvailablePacks(requestData)),
    },
    getPackModalFilterOptions: () => dispatch(getFilterOptions(availablePacksFiltersSlice)),
    getAccountPacksFilterOptions: () => dispatch(getFilterOptions(accountPacksFiltersSlice, true)),
    applyPackModalFilter: (filters: GenericFiltersMap<string | number>) =>
      dispatch(availablePacksSetAppliedFilter(filters)),
    resetPackModalFilter: () => dispatch(availablePacksResetAppliedFilter()),
    applyAccountPacksFilter: (filters: GenericFiltersMap<string | number>) =>
      dispatch(accountPacksSetAppliedFilter(filters)),
    resetAccountPacksFilter: () => dispatch(accountPacksResetAppliedFilter()),
    setAvailablePacksSearch: bindAction(availablePacksSetSearch, dispatch),
    accountPacksActions: {
      resetAccountPacksGrid: () => dispatch(resetAccountPacksGrid()),
      fetchAccountPacksGrid: (requestData: PacksRequest) => dispatch(fetchAccountPacksGrid(requestData)),
      setAccountPacksSearch: (term: string) => dispatch(setAccountPacksSearchTerm(term)),
    },
    fetchPermissionsToAccount: bindAction(fetchPermissionsToAccount, dispatch),
    addPacksToAccount: bindActionCreators(addPacksToAccount, dispatch),
    addDomainToAccount: bindActionCreators(addDomainToAccount, dispatch),
    fetchParentAccounts: bindAction(fetchParentAccounts, dispatch),
    setSelectedUsers: bindAction(setSelectedUsers, dispatch),
    resetState: () => {
      dispatch(resetParents());
      dispatch(editAccountActions.resetEditAccountInfo());
      dispatch(resetTemplateUsers());
    },
    fetchAccountsTags: bindAction(fetchAccountsTagsAction, dispatch),
    updateAddOns: bindAction(updateAddOnsThunk, dispatch),
    updateAccountAddOnsState: bindAction(setAddOns, dispatch),
    fetchAddOns: bindAction(fetchAddOnsThunk, dispatch),
    resetAddOns: bindAction(resetAddOnsAction, dispatch),
    deleteAccount: bindAction(deleteAccountThunk, dispatch),
  };
};

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withRouter(withLDConsumer()(EditFullAccount)));
export default ConnectedComponent;
